'use client';

import {
  IconSettings2,
  IconSun,
  IconMoon,
  IconDeviceLaptop
} from '@tabler/icons-react';
import { useTheme } from 'next-themes';
import { useLocale } from 'next-intl';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

import { useThemeConfig } from './active-theme';
import { useFontConfig } from './font-context';
import { FONTS } from './font.config';
import { THEMES } from './theme.config';

export function UIConfig() {
  const { activeTheme, setActiveTheme } = useThemeConfig();
  const { activeFont, activeFontArabic, setActiveFont, setActiveFontArabic } =
    useFontConfig();
  const { theme, setTheme } = useTheme();
  const locale = useLocale();
  const isArabic = locale === 'ar';

  const latinFonts = FONTS.filter((f) => f.type === 'latin');
  const arabicFonts = FONTS.filter((f) => f.type === 'arabic');

  const modes = [
    { value: 'light', label: isArabic ? 'فاتح' : 'Light', icon: IconSun },
    { value: 'dark', label: isArabic ? 'داكن' : 'Dark', icon: IconMoon },
    {
      value: 'system',
      label: isArabic ? 'نظام' : 'System',
      icon: IconDeviceLaptop
    }
  ];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant='secondary' size='icon' className='size-8'>
          <IconSettings2 className='size-4' />
          <span className='sr-only'>UI Settings</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-80'>
        <div className='grid gap-4'>
          <div className='text-sm font-medium'>
            {isArabic ? 'إعدادات الواجهة' : 'UI Settings'}
          </div>

          {/* Theme Selection */}
          <div className='grid gap-2'>
            <Label className='text-muted-foreground text-xs'>
              {isArabic ? 'السمة' : 'Theme'}
            </Label>
            <div className='grid grid-cols-2 gap-2'>
              {THEMES.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setActiveTheme(t.value)}
                  className={cn(
                    'rounded-md border px-3 py-1.5 text-xs transition-colors',
                    activeTheme === t.value
                      ? 'border-primary bg-primary/10 text-primary font-medium'
                      : 'border-border hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>

          {/* Font Selection */}
          <div className='grid gap-2'>
            <Label className='text-muted-foreground text-xs'>
              {isArabic ? 'الخط اللاتيني' : 'Font'}
            </Label>
            <Select value={activeFont} onValueChange={setActiveFont}>
              <SelectTrigger className='h-8 text-xs'>
                <SelectValue
                  placeholder={isArabic ? 'خط السمة' : 'Theme default'}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='__default__'>
                  {isArabic ? 'خط السمة' : 'Theme default'}
                </SelectItem>
                {latinFonts.map((f) => (
                  <SelectItem key={f.name} value={f.value}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Arabic Font Selection */}
          <div className='grid gap-2'>
            <Label className='text-muted-foreground text-xs'>
              {isArabic ? 'الخط العربي' : 'Arabic Font'}
            </Label>
            <Select
              value={activeFontArabic}
              onValueChange={setActiveFontArabic}
            >
              <SelectTrigger className='h-8 text-xs'>
                <SelectValue
                  placeholder={isArabic ? 'خط السمة' : 'Theme default'}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='__default__'>
                  {isArabic ? 'خط السمة' : 'Theme default'}
                </SelectItem>
                {arabicFonts.map((f) => (
                  <SelectItem key={f.name} value={f.value}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Mode Toggle */}
          <div className='grid gap-2'>
            <Label className='text-muted-foreground text-xs'>
              {isArabic ? 'الوضع' : 'Mode'}
            </Label>
            <div className='flex gap-1 rounded-md border p-1'>
              {modes.map((mode) => (
                <button
                  key={mode.value}
                  onClick={() => setTheme(mode.value)}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1.5 rounded-sm px-2 py-1 text-xs transition-colors',
                    theme === mode.value
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <mode.icon className='size-3.5' />
                  {mode.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
